import supabase from './supabase';
import type { Employee, Message } from '@/types/chat';

/**
 * Get all available users for chat
 */
export const getChatUsers = async () => {
  try {
    const { data: employees, error } = await supabase
      .from('employees')
      .select('id, empName, empAvatar, empUserId')
      .order('empName', { ascending: true });

    if (error) throw error;
    return employees as Employee[];
  } catch (error) {
    console.error('Error getting chat users:', error);
    return [];
  }
};

/**
 * Get chat messages between two users
 */
export const getChatMessages = async (currentAuthId: string, otherEmpUserId: string) => {
  try {
    const { data: messages, error } = await supabase
      .from('messages')
      .select('*')
      .or(
        `and(sender_id.eq.${currentAuthId},receiver_id.eq.${otherEmpUserId}),and(sender_id.eq.${otherEmpUserId},receiver_id.eq.${currentAuthId})`
      )
      .order('created_at', { ascending: true });

    if (error) throw error;
    return messages as Message[];
  } catch (error) {
    console.error('Error getting chat messages:', error);
    return [];
  }
};

/**
 * Send a message to another user
 */
export const sendMessage = async (
  senderAuthId: string, // UUID from auth.users.id
  receiverAuthId: string, // UUID from auth.users.id
  content: string
) => {
  try {
    console.log('Input Params:', { senderAuthId, receiverAuthId, content });

    // Validate receiver in employees table
    const { data: receiver, error: receiverError } = await supabase
      .from('employees')
      .select('empUserId')
      .eq('empUserId', receiverAuthId)
      .single();

    if (receiverError || !receiver) {
      console.error('Receiver error:', receiverError);
      throw new Error('Receiver not found or invalid empUserId.');
    }

    // Validate sender in employees table
    const { data: sender, error: senderError } = await supabase
      .from('employees')
      .select('empUserId')
      .eq('empUserId', senderAuthId)
      .single();

    if (senderError || !sender) {
      console.error('Sender error:', senderError);
      throw new Error('Sender not found or invalid empUserId.');
    }

    console.log('Validation Successful:', { sender, receiver, content });

    // Insert message into messages table
    const { data, error } = await supabase
      .from('messages')
      .insert({
        sender_id: senderAuthId, // Auth ID of the sender
        receiver_id: receiverAuthId, // Auth ID of the receiver
        content, // Message content
      })
      .select()
      .single();

    if (error) {
      console.error('Message Insert Error:', error);
      throw new Error('Failed to send the message.');
    }

    console.log('Message Inserted Successfully:', data);

    return data; // Return the inserted message
  } catch (error) {
    console.error('Error Sending Message:', error);
    throw error;
  }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (messageIds: string[]) => {
  try {
    const { error } = await supabase.from('messages').update({ read: true }).in('id', messageIds);

    if (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }

    console.log('Messages marked as read:', messageIds);
  } catch (error) {
    console.error('Error in markMessagesAsRead:', error);
    throw error;
  }
};

// Update user's online status
export const updateOnlineStatus = async (userId: string, isOnline: boolean) => {
  try {
    const { error } = await supabase
      .from('employees')
      .update({
        is_online: isOnline,
        last_seen: isOnline ? null : new Date().toISOString(),
      })
      .eq('empUserId', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating online status:', error);
  }
};

// Get unread message count
export const getUnreadMessageCount = async (userId: string, otherUserId: string) => {
  try {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('receiver_id', userId)
      .eq('sender_id', otherUserId)
      .eq('read', false);

    if (error) throw error;
    return count ?? 0;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

// Type for online status payload
type OnlineStatusPayload = {
  new: {
    empUserId: string;
    is_online: boolean;
    last_seen: string | null;
  };
};

// Subscribe to online status changes
export const subscribeToOnlineStatus = (callback: (payload: OnlineStatusPayload) => void) => {
  const channel = supabase
    .channel('online-users')
    .on('presence', { event: 'sync' }, () => {
      Object.keys(channel.presenceState()).forEach(userId => {
        callback({
          new: {
            empUserId: userId,
            is_online: true,
            last_seen: new Date().toISOString(),
          },
        });
      });
    })
    .on('presence', { event: 'join' }, ({ newPresences }) => {
      newPresences.forEach(presence => {
        callback({
          new: {
            empUserId: presence.userId as string,
            is_online: true,
            last_seen: new Date().toISOString(),
          },
        });
      });
    })
    .on('presence', { event: 'leave' }, ({ leftPresences }) => {
      leftPresences.forEach(presence => {
        callback({
          new: {
            empUserId: presence.userId as string,
            is_online: false,
            last_seen: new Date().toISOString(),
          },
        });
      });
    })
    .subscribe();

  return channel;
};

// Export supabase client for direct usage if needed
export { supabase };
